import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import Footer from '../../compos/Footer/Footer';
import Header from '../../compos/Header/Header';
import './Login.css';
import { User } from '../../compos/SinglePostHelper/SinglePostHelper';
import { useUser } from '../../UserContext';
import { useData } from '../../DataContext';
import { BACKEND_URL } from '../../config';

export type LoginProps = {
	user: User | null;
	set_user: React.Dispatch<React.SetStateAction<User | null>>;
};

type handleLoginSubmitProps = {
	user: User | null;
	set_user: React.Dispatch<React.SetStateAction<User | null>>;
	phoneNumber: string;
	password: string;
};
// Create a userInfo object
export const userInfo = {
	phoneNumber: '',
	password: '',
};

const Login = () => {
	const location = useLocation();
	const [phoneNumber, set_phoneNumber] = useState<string>('');
	const [password, set_password] = useState<string>('');
	const { user, set_user, setIsAdmin, setIsLoggedIn, getPostId, isAuthLoading } = useUser();
	const { clearData } = useData();
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [showPopup, setShowPopup] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>(
		'Incorrect phone number or password.'
	);

	useEffect(() => {
		setTimeout(() => {
			window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
		}, 10);
	}, []);

	const togglePasswordVisibility = () => {
		setIsPasswordVisible(!isPasswordVisible);
	};

	// Maps backend errorCode → user-facing copy. Falls back to the server's
	// `message`, then a generic line, so a missing/older backend still works.
	const messageForLoginError = (
		status: number | null,
		errorCode: string | undefined,
		message: string | undefined,
	): string => {
		if (errorCode === 'PHONE_NOT_FOUND')
			return 'No account is registered with this phone number.';
		if (errorCode === 'WRONG_PASSWORD')
			return 'Incorrect password. Please try again.';
		if (errorCode === 'INVALID_INPUT')
			return 'Phone number and password are required.';
		if (errorCode === 'TOO_MANY_REQUESTS' || status === 429)
			return 'Too many attempts. Please wait and try again.';
		if (status && status >= 500)
			return 'Server error. Please try again later.';
		if (message && message.trim().length > 0) return message;
		return 'Incorrect phone number or password.';
	};

	const handleLoginSubmit = async ({ user, set_user, phoneNumber, password }: handleLoginSubmitProps) => {
		setShowPopup(false);

		// Lightweight client-side guard so we don't even hit the backend for
		// obviously-invalid input.
		if (!phoneNumber || phoneNumber.length !== 10) {
			setErrorMessage('Phone number must be 10 digits.');
			setShowPopup(true);
			return;
		}
		if (!password) {
			setErrorMessage('Please enter your password.');
			setShowPopup(true);
			return;
		}

		var urlencoded = new URLSearchParams();
		urlencoded.append('phoneNumber', '91' + phoneNumber);
		urlencoded.append('password', password);

		const requestOptions = {
			method: 'POST',
			body: urlencoded,
			redirect: 'follow' as RequestRedirect,
			credentials: 'include' as RequestCredentials,
		};

		try {
			const loginResponse = await fetch(`${BACKEND_URL}/auth/loginWeb`, requestOptions);

			let loginData: any = null;
			try {
				loginData = await loginResponse.json();
			} catch {
				loginData = null;
			}

			if (loginResponse.ok && loginData && (loginData.accessToken || loginData.token)) {
				// The JWT now lives in an httpOnly cookie set by loginWeb — we never
				// store it in JS-readable localStorage. Keep only a non-sensitive flag
				// so the UI knows it's logged in (and cross-tab sync still works).
				localStorage.removeItem('authToken'); // drop any legacy token so no stale Bearer is sent
				localStorage.setItem('isAuthed', '1');
				clearData();

				const profileRequestOptions = {
					method: 'GET',
					redirect: 'follow' as RequestRedirect,
					credentials: 'include' as RequestCredentials,
				};

				const profileResponse = await fetch(`${BACKEND_URL}/user/myProfile`, profileRequestOptions);

				if (profileResponse.ok) {
					const result = await profileResponse.json();

					if (result.id) {
						set_user(result);
						setIsLoggedIn(true);
						await checkIfAdmin(setIsAdmin);
					}
				} else {
					console.log('Error occurred while fetching profile:', profileResponse.status);
				}
			} else {
				setErrorMessage(
					messageForLoginError(
						loginResponse.status,
						loginData?.errorCode,
						loginData?.message,
					),
				);
				setShowPopup(true);
			}
		} catch (error) {
			setErrorMessage('Cannot reach the server. Check your internet and try again.');
			setShowPopup(true);
		}
	};

	const checkIfAdmin = async (setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>) => {
		try {
			const response = await fetch(`${BACKEND_URL}/api/isAdmin`, {
				method: 'GET',
				credentials: 'include',
			});

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const data = await response.json();

			// Check if the API response indicates the user is an admin
			setIsAdmin(data);
		} catch (error) {
			console.error('There was a problem with the fetch operation:', error);
			setIsAdmin(false);
		}
	};

	if (isAuthLoading) {
		return (
			<div className='flex justify-center items-center h-screen'>
				<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#022c56]'></div>
			</div>
		);
	}

	if (getPostId !== '' && user != null) {
		return (
			<div>
				<Navigate to={`/getpost/${getPostId}`} />
			</div>
		);
	} else if (user != null) {
		const destination = location.state?.from || '/reports';
		return (
			<div>
				<Navigate to={destination} />
			</div>
		);
	} else {
		return (
			<div className='login-bg d-flex flex-column justify-content-between'>
				<Header />
				{/* <div className='container login-card-container'>
					<div className='card login-card mx-auto shadow '>
						<div className='card-body'>
							<h1 className='card-title mt-1'>Login</h1>
							<Loginform user={user} set_user={set_user} />
						</div>
					</div>
				</div> */}
				<section className='user'>
					<div className='user_options-container'>
						<div className='user_options-text'>
							<div className='user_options-unregistered'>
								<h2 className='user_unregistered-title'>New to My News My Report?</h2>
								<p className='user_unregistered-text'>Create an account to join Now!</p>
								<Link
									className='user_unregistered-signup no-underline'
									id='signup-button'
									to='/signup'
									state={location.state}
								>
									Sign Up
								</Link>
							</div>
						</div>

						<div className='user_options-forms' id='user_options-forms'>
							<div className='user_forms-login'>
								<h2 className='forms_title'>Login</h2>
								<form className='forms_form flex flex-col gap-[20px] w-full m-0 p-0'>
									<fieldset className='forms_fieldset flex flex-col gap-[20px] w-full border-0 p-0 m-0'>
										<label className='flex items-center w-full border border-gray-300 rounded bg-white px-2 py-3 focus-within:border-[#022c56] transition-colors duration-200 cursor-text m-0'>
											<i className='fa fa-solid fa-user text-gray-400 mx-3 text-lg'></i>
											<div className='w-px h-6 bg-gray-300 mr-3'></div>
											<input
												placeholder='Phone (+91)'
												className='flex-1 w-full bg-transparent border-none text-gray-800 font-light placeholder-gray-400 no-focus-outline'
												style={{ outline: 'none', boxShadow: 'none' }}
												required
												autoFocus
												onChange={(e) => set_phoneNumber(e.target.value)}
												type='text'
												pattern='\d*'
												maxLength={10}
												minLength={10}
											/>
										</label>

										<label className='flex items-center w-full border border-gray-300 rounded bg-white px-2 py-3 focus-within:border-[#022c56] transition-colors duration-200 cursor-text m-0'>
											<i className='fa fa-solid fa-key text-gray-400 mx-3 text-lg'></i>
											<div className='w-px h-6 bg-gray-300 mr-3'></div>
											<input
												type={isPasswordVisible ? 'text' : 'password'}
												placeholder='Password'
												className='flex-1 w-full bg-transparent border-none text-gray-800 font-light placeholder-gray-400 no-focus-outline'
												style={{ outline: 'none', boxShadow: 'none' }}
												required
												onChange={(e) => set_password(e.target.value)}
											/>
											<button
												type='button'
												onClick={(e) => {
													e.preventDefault();
													togglePasswordVisibility();
												}}
												className='text-gray-400 hover:text-[#022c56] transition-colors outline-none focus:outline-none px-3 flex-shrink-0 cursor-pointer'
											>
												<i className={`bi ${isPasswordVisible ? 'bi-eye' : 'bi-eye-slash'} text-xl`}></i>
											</button>
										</label>
									</fieldset>
									<div className='login-error-slot'>
										{showPopup && (
											<span className='login-error-text'>
												<i className='fa fa-solid fa-circle-exclamation'></i>
												{errorMessage}
											</span>
										)}
									</div>
									<div className='forms_buttons m-0 w-full'>
										<input
											type='submit'
											value='Log In'
											className='forms_buttons-action m-0'
											onClick={(e) => {
												e.preventDefault();
												handleLoginSubmit({ user, set_user, phoneNumber, password });
											}}
										/>
									</div>
								</form>
							</div>
						</div>
					</div>
				</section>
				<Footer />
			</div>
		);
	}
};

export default Login;
